import React, { useEffect, useMemo, useRef, useState } from 'react';
import { collection, doc, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../firebase/config';

interface StudentRow {
    id: string;
    name: string;
    admissionNo?: string;
    uid?: string;
}

interface ActivityLog {
    id: string;
    message: string;
    status: string;
    type: string;
    uid?: string;
    studentDocId?: string;
    createdAtLabel: string;
}

const StudentAttendanceStream: React.FC = () => {
    const now = new Date();
    const [selectedYear, setSelectedYear] = useState(now.getFullYear());
    const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
    const [students, setStudents] = useState<StudentRow[]>([]);
    const [attendanceByUid, setAttendanceByUid] = useState<Record<string, Record<string, string>>>({});
    const [activityLogs, setActivityLogs] = useState<ActivityLog[]>([]);
    const monthUnsubsRef = useRef<Array<() => void>>([]);

    const daysInMonth = useMemo(() => new Date(selectedYear, selectedMonth, 0).getDate(), [selectedYear, selectedMonth]);
    const dayColumns = useMemo(() => Array.from({ length: daysInMonth }, (_, i) => String(i + 1)), [daysInMonth]);

    useEffect(() => {
        const studentsRef = collection(db, 'students');
        const unsubStudents = onSnapshot(studentsRef, (snapshot) => {
            const rows: StudentRow[] = snapshot.docs.map((studentDoc) => {
                const payload = studentDoc.data() as any;
                const firstName = payload?.firstName || '';
                const lastName = payload?.lastName || '';
                const fallbackName = payload?.fullName || payload?.name || 'Unnamed Student';
                return {
                    id: studentDoc.id,
                    name: `${firstName} ${lastName}`.trim() || fallbackName,
                    admissionNo: payload?.academicInfo?.admissionNo || payload?.studentId,
                    uid: payload?.rfidUID || undefined,
                };
            });
            setStudents(rows);
        });

        return () => {
            unsubStudents();
        };
    }, []);

    useEffect(() => {
        monthUnsubsRef.current.forEach((unsub) => unsub());
        monthUnsubsRef.current = [];
        setAttendanceByUid({});

        const monthKey = String(selectedMonth).padStart(2, '0');
        students.forEach((student) => {
            if (!student.uid) {
                return;
            }
            const monthDocRef = doc(
                db,
                'attendance',
                'students',
                'uid',
                student.uid,
                'year',
                String(selectedYear),
                'month',
                monthKey
            );

            const unsub = onSnapshot(monthDocRef, (monthSnap) => {
                if (!monthSnap.exists()) {
                    setAttendanceByUid((prev) => {
                        const next = { ...prev };
                        delete next[student.uid as string];
                        return next;
                    });
                    return;
                }
                const data = monthSnap.data() as Record<string, any>;
                const dayMap: Record<string, string> = {};
                Object.keys(data).forEach((key) => {
                    if (/^\d+$/.test(key)) {
                        const value = data[key];
                        dayMap[key] = typeof value === 'string' ? value : value?.stringValue || '';
                    }
                });
                setAttendanceByUid((prev) => ({
                    ...prev,
                    [student.uid as string]: dayMap,
                }));
            });
            monthUnsubsRef.current.push(unsub);
        });

        return () => {
            monthUnsubsRef.current.forEach((unsub) => unsub());
            monthUnsubsRef.current = [];
        };
    }, [students, selectedMonth, selectedYear]);

    useEffect(() => {
        const logsRef = collection(db, 'activity_logs');
        const logsQuery = query(logsRef, where('type', 'in', ['attendance', 'registration']));
        const unsubLogs = onSnapshot(logsQuery, (snapshot) => {
            const rows: ActivityLog[] = snapshot.docs
                .map((logDoc) => {
                    const payload = logDoc.data() as any;
                    const createdAt = payload?.createdAt?.toDate ? payload.createdAt.toDate() : new Date();
                    return {
                        id: logDoc.id,
                        message: payload?.message || 'No message',
                        status: payload?.status || 'info',
                        type: payload?.type || 'unknown',
                        uid: payload?.uid || undefined,
                        studentDocId: payload?.studentDocId || undefined,
                        createdAtLabel: createdAt.toLocaleString(),
                    };
                })
                .sort((a, b) => (a.createdAtLabel < b.createdAtLabel ? 1 : -1))
                .slice(0, 50);
            setActivityLogs(rows);
        });

        return () => {
            unsubLogs();
        };
    }, []);

    const getTotals = (dayMap: Record<string, string>) => {
        let present = 0;
        let leaves = 0;
        dayColumns.forEach((day) => {
            const value = (dayMap[day] || '').toLowerCase();
            if (value === 'p') present += 1;
            if (value === 'l') leaves += 1;
        });
        const absent = Math.max(0, dayColumns.length - present - leaves);
        return { present, absent, leaves };
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Student Attendance Stream</h1>
                    <p className="text-sm text-gray-600">Live Firestore stream for attendance and registration activity</p>
                </div>
                <div className="flex items-center gap-3">
                    <select
                        value={selectedMonth}
                        onChange={(e) => setSelectedMonth(Number(e.target.value))}
                        className="px-3 py-2 border border-gray-300 rounded-lg bg-white"
                    >
                        {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
                            <option key={month} value={month}>
                                {month.toString().padStart(2, '0')}
                            </option>
                        ))}
                    </select>
                    <input
                        type="number"
                        value={selectedYear}
                        onChange={(e) => setSelectedYear(Number(e.target.value))}
                        className="w-28 px-3 py-2 border border-gray-300 rounded-lg"
                    />
                </div>
            </div>

            <section className="card p-4 overflow-auto">
                <table className="min-w-full text-xs border-collapse">
                    <thead>
                        <tr>
                            <th className="sticky left-0 bg-white px-2 py-2 border text-left">Student</th>
                            {dayColumns.map((day) => (
                                <th key={day} className="px-2 py-2 border">{day}</th>
                            ))}
                            <th className="px-2 py-2 border">Present</th>
                            <th className="px-2 py-2 border">Absent</th>
                            <th className="px-2 py-2 border">Leaves</th>
                        </tr>
                    </thead>
                    <tbody>
                        {students.map((student) => {
                            const dayMap = student.uid ? (attendanceByUid[student.uid] || {}) : {};
                            const totals = getTotals(dayMap);
                            return (
                                <tr key={student.id}>
                                    <td className="sticky left-0 bg-white px-2 py-2 border whitespace-nowrap">
                                        <div className="font-medium text-gray-900">{student.name}</div>
                                        <div className="text-gray-500">
                                            {student.admissionNo || student.id}
                                            {student.uid ? ` | UID: ${student.uid}` : ' | No UID'}
                                        </div>
                                    </td>
                                    {dayColumns.map((day) => {
                                        const value = (dayMap[day] || '').toLowerCase();
                                        return (
                                            <td key={`${student.id}-${day}`} className="px-2 py-2 border text-center uppercase">
                                                {value || '-'}
                                            </td>
                                        );
                                    })}
                                    <td className="px-2 py-2 border text-center font-semibold text-green-700">{totals.present}</td>
                                    <td className="px-2 py-2 border text-center font-semibold text-red-700">{totals.absent}</td>
                                    <td className="px-2 py-2 border text-center font-semibold text-yellow-700">{totals.leaves}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </section>

            <section className="card p-4">
                <h2 className="text-lg font-semibold text-gray-900 mb-3">Live Activity Logs</h2>
                <div className="max-h-96 overflow-auto divide-y divide-gray-100">
                    {activityLogs.length === 0 ? (
                        <p className="text-sm text-gray-600 py-2">No activity yet.</p>
                    ) : (
                        activityLogs.map((log) => (
                            <div key={log.id} className="py-2 text-sm">
                                <p className="text-gray-900">
                                    <span className="font-semibold uppercase">{log.type}</span>
                                    {' '}
                                    <span className={log.status === 'success' ? 'text-green-700' : log.status === 'failed' ? 'text-red-700' : 'text-gray-700'}>
                                        ({log.status})
                                    </span>
                                    : {log.message}
                                </p>
                                <p className="text-xs text-gray-600">
                                    {log.createdAtLabel}
                                    {log.uid ? ` | UID: ${log.uid}` : ''}
                                    {log.studentDocId ? ` | Student: ${log.studentDocId}` : ''}
                                </p>
                            </div>
                        ))
                    )}
                </div>
            </section>
        </div>
    );
};

export default StudentAttendanceStream;
